import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  RaumtempRaumzeile, RaumtempStand,
} from '../api';
import type { Rahmen } from './Projektrahmen';

/**
 * Heiz-/Kühllast → Raumtemperaturen (Paket RT1).
 *
 * ═══ Warum diese Seite ═══════════════════════════════════════════════════
 *
 * Die Heizlast nach DIN EN 12831-1 beginnt mit der Solltemperatur des Raums,
 * die Kühllast nach VDI 2078 ebenso. Bis RT1 stand nirgends, WOHER diese Zahl
 * kommt — sie stand in `heizlast_raumwert` und war damit eine Zahl ohne
 * Geschichte.
 *
 * Diese Seite zeigt die Geschichte: die Vorlage des Fachplaners vom
 * 26.06.2026 mit ihren Bändern, die Regel, über die ein Raum ihr zugeordnet
 * wurde, und den Abgleich gegen das, was vorher dastand.
 *
 * ═══ Was auf einen Blick erkennbar sein muss ═════════════════════════════
 *
 * Die drei Herkünfte, und zwar in dieser Reihenfolge der Dringlichkeit:
 *
 *   fehlt  — der Raum hat KEINEN belegten Sollwert aus RT1. Rot. Ein
 *            erfundener Sollwert wäre schlimmer als diese sichtbare Lücke,
 *            weil die Rechnung ihn nicht von einem belegten unterscheidet.
 *   norm   — der Wert stammt aus einer Norm und nicht aus der Vorlage. Gelb.
 *            Er gilt der Nutzungsklasse, nicht diesem Raum.
 *   fachplaner — aus der Vorlage. Grün.
 *
 * Daneben steht die WIRKUNG: ob der Wert in der Rechnung ankommt oder ob der
 * Bestand den besseren Rang hat und stehen bleibt. „Weicht ab" und „wird
 * übernommen" sind zwei verschiedene Aussagen.
 */
export function RaumtemperaturenSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [stand, setStand] = useState<RaumtempStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filter, setFilter] = useState<'alle' | 'fachplaner' | 'norm' | 'fehlt'
                                     | 'abweichung'>('alle');

  useEffect(() => {
    let abgebrochen = false;

    (async () => {
      try {
        const s = await api.raumtemperaturen(projekt.id);
        if (!abgebrochen) setStand(s);
      } catch (f) {
        if (!abgebrochen) setFehler(String(f));
      }
    })();

    return () => { abgebrochen = true; };
  }, [api, projekt.id]);

  const raeume = useMemo(() => {
    const alle = stand?.raeume ?? [];

    if (filter === 'alle') return alle;
    if (filter === 'abweichung') {
      return alle.filter((z) => z.abgleich === 'abweichung');
    }

    return alle.filter((z) => z.herkunft === filter);
  }, [stand, filter]);

  const q = stand?.quote;

  return (
    <>
      <div className="karte">
        <div className="bereich-kopf">
          <h2 className="karte-titel" style={{ margin: 0 }}>
            Raumtemperaturen — die Vorlage des Fachplaners
          </h2>
          {stand && (
            <span className={marke(q?.fehlt ?? 0)}>
              {(q?.fehlt ?? 0) === 0
                ? 'kein Raum ohne Wert'
                : `${q?.fehlt} Raum/Räume ohne belegten Wert`}
            </span>
          )}
        </div>

        {fehler && <p className="fehler">{fehler}</p>}

        <p className="schwach" style={{ marginTop: 8 }}>
          Die Solltemperatur ist die erste Zahl jeder Last&shy;rechnung. Hier
          steht, woher sie kommt: aus welcher Zeile der Vorlage, über welche
          Regel, aus welchem Band — und was vorher dastand.
        </p>

        {stand && (
          <p style={{ marginTop: 8 }}>
            <strong>Festlegung F70:</strong> die Heizlast rechnet mit dem{' '}
            <strong>{stand.bandlageHeizen === 'oben' ? 'oberen' : 'unteren'}</strong>{' '}
            Wert des Winterbandes, die Kühllast mit dem{' '}
            <strong>{stand.bandlageKuehlen === 'unten' ? 'unteren' : 'oberen'}</strong>{' '}
            Wert des Sommerbandes. Das Band bleibt daneben gespeichert — aus
            „21–22 °C" wird nie 21,5 °C.
          </p>
        )}

        {q && (
          <div style={{ overflowX: 'auto', marginTop: 14 }}>
            <table className="liste">
              <thead>
                <tr>
                  <th>Herkunft</th>
                  <th style={{ textAlign: 'right' }}>Räume</th>
                  <th>Was sie bedeutet</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="marke gut">fachplaner</span></td>
                  <td style={{ textAlign: 'right' }}>
                    {q.fachplaner.toLocaleString('de-DE')}
                  </td>
                  <td>Der Raum fällt unter einen Bereich der Vorlage.</td>
                </tr>
                <tr>
                  <td><span className="marke warnung">norm</span></td>
                  <td style={{ textAlign: 'right' }}>
                    {q.norm.toLocaleString('de-DE')}
                  </td>
                  <td>
                    Kein Bereich der Vorlage, aber ein Normwert für die
                    Nutzung. Er gilt der Klasse, nicht diesem Raum.
                  </td>
                </tr>
                <tr>
                  <td><span className="marke schlecht">fehlt</span></td>
                  <td style={{ textAlign: 'right' }}>
                    {q.fehlt.toLocaleString('de-DE')}
                  </td>
                  <td>
                    Weder noch — <strong>kein Wert</strong>. Gezählt und
                    benannt statt erfunden.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {q && (
          <p className="schwach" style={{ marginTop: 10 }}>
            Abgleich gegen den Bestand: {q.gleich.toLocaleString('de-DE')}{' '}
            gleich, {q.abweichung.toLocaleString('de-DE')} abweichend,{' '}
            {q.neu.toLocaleString('de-DE')} ohne Bestandswert. Davon kommen{' '}
            {q.gesetzt.toLocaleString('de-DE')} in der Rechnung an;{' '}
            {q.behalten.toLocaleString('de-DE')} behalten ihren Bestandswert,
            weil er den besseren Rang hat.
          </p>
        )}
      </div>

      {/* ── Die Vorlage ────────────────────────────────────────────────── */}
      <div className="karte">
        <h3 className="karte-titel">
          Die Vorlage: {(stand?.bereiche ?? []).length} Bereiche
        </h3>

        {stand && stand.bereiche.length > 0 && (
          <p className="schwach" style={{ marginTop: 4 }}>
            {stand.bereiche[0].quelle}
          </p>
        )}

        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table className="liste">
            <thead>
              <tr>
                <th>Bereich</th>
                <th>Nutzung / Einordnung</th>
                <th style={{ textAlign: 'right' }}>Winter °C</th>
                <th style={{ textAlign: 'right' }}>Sommer °C</th>
                <th style={{ textAlign: 'right' }}>r. F. %</th>
                <th>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {(stand?.bereiche ?? []).map((b) => (
                <tr key={b.code}>
                  <td>
                    {b.bereich}
                    {!b.istAuslegungsfall && (
                      <>
                        {' '}
                        <span className="marke neutral">
                          kein Auslegungsfall
                        </span>
                      </>
                    )}
                  </td>
                  <td className="schwach">{b.nutzung ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {band(b.winterUntenC, b.winterObenC)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {band(b.sommerUntenC, b.sommerObenC)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {band(b.feuchteUntenProzent, b.feuchteObenProzent)}
                  </td>
                  <td className="schwach">{b.hinweis ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(stand?.hinweise ?? []).length > 0 && (
          <ul className="bereich-stand" style={{ marginTop: 12 }}>
            {(stand?.hinweise ?? []).map((h) => (
              <li key={h.schluessel}>{h.wortlaut}</li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Die Räume ─────────────────────────────────────────────────── */}
      <div className="karte">
        <div className="bereich-kopf">
          <h3 className="karte-titel" style={{ margin: 0 }}>
            Die Räume ({raeume.length.toLocaleString('de-DE')})
          </h3>

          <div className="unterreiter">
            {([
              ['alle', 'alle'],
              ['fachplaner', 'fachplaner'],
              ['norm', 'norm'],
              ['fehlt', 'ohne Wert'],
              ['abweichung', 'nur Abweichungen'],
            ] as const).map(([wert, text]) => (
              <button
                key={wert}
                type="button"
                className={filter === wert ? 'aktiv' : undefined}
                onClick={() => setFilter(wert)}
              >
                {text}
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table className="liste">
            <thead>
              <tr>
                <th>Raum</th>
                <th>Name</th>
                <th>Herkunft</th>
                <th>Bereich / Regel</th>
                <th style={{ textAlign: 'right' }}>Heizen °C</th>
                <th style={{ textAlign: 'right' }}>Kühlen °C</th>
                <th style={{ textAlign: 'right' }}>vorher °C</th>
                <th style={{ textAlign: 'right' }}>Δ K</th>
                <th>Wirkung</th>
              </tr>
            </thead>
            <tbody>
              {raeume.map((z) => (
                <tr key={z.roomId} title={hilfe(z)}>
                  <td>{z.raumnummer ?? '—'}</td>
                  <td>{z.raumname ?? '—'}</td>
                  <td><span className={herkunftsmarke(z.herkunft)}>{z.herkunft}</span></td>
                  <td className="schwach">
                    {z.bereich ?? z.regel ?? '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{zahl(z.tempHeizenC)}</td>
                  <td style={{ textAlign: 'right' }}>{zahl(z.tempKuehlenC)}</td>
                  <td style={{ textAlign: 'right' }} className="schwach">
                    {zahl(z.bestandTempHeizenC)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {zahl(z.abweichungHeizenK)}
                  </td>
                  <td className="schwach">{z.wirkung ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {raeume.length === 0 && (
          <p className="schwach" style={{ marginTop: 10 }}>
            Keine Zeile. Die Zuordnung entsteht mit dem Werkzeug{' '}
            <code>raumtemperaturen-zuordnen &lt;projektId&gt; --schreiben</code>.
          </p>
        )}
      </div>
    </>
  );
}

function band(unten: number | null, oben: number | null): string {
  if (unten === null && oben === null) return '—';
  if (unten === null) return String(oben);
  if (oben === null) return String(unten);
  return unten === oben ? String(unten) : `${unten}–${oben}`;
}

function zahl(w: number | null): string {
  return w === null || w === undefined
    ? '—'
    : w.toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

function herkunftsmarke(herkunft: string): string {
  if (herkunft === 'fachplaner') return 'marke gut';
  if (herkunft === 'norm') return 'marke warnung';
  return 'marke schlecht';
}

function marke(fehlt: number): string {
  return fehlt === 0 ? 'marke gut' : 'marke warnung';
}

function hilfe(z: RaumtempRaumzeile): string {
  const teile = [
    z.regel ? `Regel ${z.regel}` : null,
    z.muster ? `Muster ${z.muster}` : null,
    z.guete ? `Güte ${z.guete}` : null,
    z.quelle,
    z.wirkungGrund,
    z.begruendung,
  ].filter(Boolean);

  return teile.join(' · ');
}
